import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Weight,
  Route as RouteIcon,
  Clock,
  Package,
  MapPin,
  Play,
  Pause,
  CheckCircle2,
  Minus,
  Plus,
  Navigation,
  ChevronDown,
  AlertTriangle,
  Info,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getDriverWork,
  refreshDriverWork,
  startWork,
  pauseWork,
  resumeWork,
  completeWork,
  roundLoaded,
  roundUnloaded,
  recordStep,
  updateMetric,
  addService,
  updateServiceQuantity,
} from "../../store/slices/worksSlice";
import { getServices } from "../../store/slices/servicesSlice";
import { GeoCoordinates, ServiceLine, WorkEventType } from "../../types/works";
import { directoryServiceToType } from "../../constants/worksConstants";
import { WorkStatusBadge, WorkTypeBadge } from "../../components/works/badges";
import { LocationView } from "../../components/works/LocationView";
import {
  formatHours,
  getServiceIcon,
  getVehicleLabel,
} from "../../components/works/utils";
import {
  autoDistance,
  autoTonnage,
  elapsedHours,
  formatCoords,
  formatTime,
  nextPickupDeliveryStep,
} from "../../components/works/execUtils";
import { useGeoStamp } from "../../components/works/useGeoStamp";
import { MetricEditConfig, MetricEditSheet } from "../../components/works/driver/MetricEditSheet";
import { ServicePicker } from "../../components/works/driver/ServicePicker";
import { RoundCloseSheet, RoundCloseData } from "../../components/works/driver/RoundCloseSheet";
import { Modal } from "../../components/UI/Modal";
import Button from "../../components/UI/Button";
import { useSocketEvent, useSocketResync } from "../../hooks/useSocketEvent";

type MetricKey = "tonnage" | "distanceKm" | "hours";

/** Attach geo coords to an execution payload. */
const withCoords = (coords?: GeoCoordinates, extra: Record<string, unknown> = {}) => ({
  latitude: coords?.latitude,
  longitude: coords?.longitude,
  ...extra,
});

export const DriverWorkExecutionPage: React.FC = () => {
  const { t } = useTranslation("works");
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { capture, capturing } = useGeoStamp();

  const work = useAppSelector((state) => state.works.currentWork);
  const loading = useAppSelector((state) => state.works.loading);
  const error = useAppSelector((state) => state.works.error);
  const directoryServices = useAppSelector((state) => state.services.services);
  const servicesLoaded = useAppSelector((state) => state.services.isLoaded);

  const [metricConfig, setMetricConfig] = useState<MetricEditConfig | null>(null);
  const [servicePickerOpen, setServicePickerOpen] = useState(false);
  const [roundSheetOpen, setRoundSheetOpen] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  // Mirror `busy` into a ref so the poll can skip while an action is in flight
  // without re-creating the interval on every busy toggle.
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (id) dispatch(getDriverWork(id));
  }, [dispatch, id]);

  // Load the company's Services directory so the driver's service pickers offer
  // real catalogue entries (filtered to this work's client below), mirroring the
  // shipment form's "Additional service" box.
  useEffect(() => {
    dispatch(getServices({ limit: 1000 }));
  }, [dispatch]);

  // Services offered to the driver: the work's client entries when it has a
  // client (customerId ↔ service.partnerId), otherwise the General (unassigned)
  // ones — same rule as the shipment form's partner filter. When the client has
  // no services of its own we transparently fall back to General so the driver
  // always has a catalogue to use; the driver never sees which bucket won (no
  // client/general labelling anywhere), it's just "the services for this work".
  const serviceOptions = useMemo(() => {
    const general = directoryServices.filter((s) => s.partnerId == null);
    const customerId = work?.customerId;
    const clientScoped = customerId
      ? directoryServices.filter((s) => s.partnerId === customerId)
      : [];
    const resolved = customerId
      ? clientScoped.length > 0
        ? clientScoped
        : general
      : general;
    return resolved.map(directoryServiceToType);
  }, [directoryServices, work?.customerId]);

  // The dispatcher can edit this work (e.g. add planned services) from a
  // separate session. The server now pushes `work:changed` to the assigned
  // driver's own room (drivers are never in the company room), so the 15s poll
  // is gone. The busyRef guard is kept from the poll: refetching mid-action
  // would clobber the result that's about to land.
  useSocketEvent<{ _id?: string }>("work:changed", (changed) => {
    if (!id || document.hidden || busyRef.current) return;
    if (changed?._id && changed._id !== id) return;
    dispatch(refreshDriverWork(id));
  });

  useEffect(() => {
    if (!id) return;
    const refetch = () => {
      if (!document.hidden && !busyRef.current) dispatch(refreshDriverWork(id));
    };
    window.addEventListener("focus", refetch);
    return () => window.removeEventListener("focus", refetch);
  }, [dispatch, id]);

  // Drivers run this offline-capable; a reconnect may follow a long gap.
  useSocketResync(() => {
    if (id && !busyRef.current) dispatch(refreshDriverWork(id));
  });

  // Keep auto-tracked hours ticking while the work is running.
  useEffect(() => {
    if (work?.status !== "in_progress") return;
    const handle = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(handle);
  }, [work?.status]);

  const started = !!work?.actualStart;
  const isCompleted = work?.status === "completed";

  const hoursActual = useMemo(() => {
    if (!work) return undefined;
    if (work.hours.autoCalcDisabled) return work.hours.actual;
    return elapsedHours(work);
  }, [work]);

  const isReady = !!work && work._id === id;

  if (!isReady) {
    // Only show "not found" once the fetch has actually failed. Until then —
    // while loading, or before the effect has even dispatched on first paint —
    // show a spinner so the page doesn't flash the not-found message.
    if (error && !loading) {
      return (
        <div className="min-h-screen bg-[#FAFAF8] flex flex-col items-center justify-center px-4 text-center">
          <p className="text-sm font-medium text-[#2B2F2E]">{t("driver.exec.notFound")}</p>
          <button
            onClick={() => navigate("/works")}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-[#C97A3C] text-white text-sm font-medium rounded-lg"
          >
            <ArrowLeft className="h-4 w-4" /> {t("driver.exec.backToList")}
          </button>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[#FAFAF8] flex flex-col items-center justify-center px-4 text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#C97A3C] border-t-transparent" />
      </div>
    );
  }

  const hasRoute = !!work.secondaryObject;
  const tonnageActual = work.tonnage.actual ?? autoTonnage(work);
  const distanceActual = work.distanceKm.actual ?? autoDistance(work);

  // ---- geo-stamped action wrappers ----------------------------------------
  // Each action awaits the thunk via .unwrap() so a rejection (e.g. the work
  // was completed elsewhere, or a network/403 error) surfaces as an error toast
  // instead of a false success.
  const runAction = async (
    thunkResult: { unwrap: () => Promise<unknown> },
    onSuccess: () => void,
  ) => {
    setBusy(true);
    try {
      await thunkResult.unwrap();
      onSuccess();
    } catch (e) {
      toast.error(typeof e === "string" ? e : t("driver.toasts.actionFailed"));
    } finally {
      setBusy(false);
    }
  };

  const handleStart = async () => {
    const coords = await capture();
    await runAction(
      dispatch(startWork({ id: work._id, data: withCoords(coords, { label: t("driver.events.start") }) })),
      () => toast.success(t("driver.toasts.started")),
    );
  };

  const handleRoundLoaded = async () => {
    const coords = await capture();
    const n = (work.roundsActual ?? 0) + 1;
    await runAction(
      dispatch(roundLoaded({ id: work._id, data: withCoords(coords, { label: t("driver.events.round_loaded", { n }) }) })),
      () => toast.success(t("driver.toasts.roundLoaded", { n })),
    );
  };

  // Unloading a round now goes through the capture sheet — the driver must enter
  // this round's real tonnage/mileage (and any services) before it is recorded.
  const submitRoundClose = async (data: RoundCloseData) => {
    const coords = await capture();
    const n = (work.roundsActual ?? 0) + 1;
    const isFinalRound = !!work.roundsPlanned && n >= work.roundsPlanned;
    await runAction(
      dispatch(
        roundUnloaded({
          id: work._id,
          data: withCoords(coords, {
            label: t("driver.events.round_unloaded", { n }),
            tonnage: data.tonnage,
            distanceKm: data.distanceKm,
            services: data.services,
          }),
        }),
      ),
      () => {
        toast.success(t("driver.toasts.roundDone", { n }));
        setRoundSheetOpen(false);
        // After the last planned round, prompt to finalize the work.
        if (isFinalRound) setCompleteConfirmOpen(true);
      },
    );
  };

  const handlePause = async () => {
    const coords = await capture();
    await runAction(
      dispatch(pauseWork({ id: work._id, data: withCoords(coords, { label: t("driver.events.pause") }) })),
      () => toast.info(t("driver.toasts.paused")),
    );
  };

  const handleResume = async () => {
    const coords = await capture();
    await runAction(
      dispatch(resumeWork({ id: work._id, data: withCoords(coords, { label: t("driver.events.resume") }) })),
      () => toast.success(t("driver.toasts.resumed")),
    );
  };

  const handleStep = async (step: WorkEventType) => {
    const coords = await capture();
    await runAction(
      dispatch(recordStep({ id: work._id, data: withCoords(coords, { step, label: t(`driver.steps.${step}`) }) })),
      () => toast.success(t("driver.toasts.stepDone", { step: t(`driver.steps.${step}`) })),
    );
  };

  const handleComplete = async (hours?: number, tonnage?: number) => {
    const coords = await capture();
    setBusy(true);
    try {
      // pickup_delivery may record an optional tonnage right before finishing —
      // persist it first so it's part of the completed work.
      if (tonnage != null) {
        await dispatch(
          updateMetric({ id: work._id, data: { metric: "tonnage", value: tonnage } }),
        ).unwrap();
      }
      await dispatch(
        completeWork({
          id: work._id,
          data: withCoords(coords, {
            label: t("driver.events.complete"),
            ...(hours != null ? { hours } : {}),
          }),
        }),
      ).unwrap();
      toast.success(t("driver.toasts.completed"));
      setCompleteConfirmOpen(false);
    } catch (e) {
      toast.error(typeof e === "string" ? e : t("driver.toasts.actionFailed"));
    } finally {
      setBusy(false);
    }
  };

  // ---- metric editor -------------------------------------------------------
  // Only non-cyclic tonnage/distance route here. Cyclic tonnage/distance are
  // captured per round (round-close sheet) and hours is finalized at completion,
  // so neither opens the free editor.
  const openMetricEditor = (metricKey: MetricKey) => {
    if (isCompleted || work.type === "cyclic" || metricKey === "hours") return;
    const pure: Record<MetricKey, { actual?: number; planned?: number; unit: string }> = {
      tonnage: { actual: tonnageActual, planned: work.tonnage.planned, unit: "t" },
      distanceKm: { actual: distanceActual, planned: work.distanceKm.planned, unit: "km" },
      hours: { actual: hoursActual, planned: work.hours.planned, unit: "h" },
    };
    const meta = pure[metricKey];

    const icons: Record<MetricKey, { icon: React.ReactNode; cls: string; title: string; step: number; quick: number[] }> = {
      tonnage: {
        icon: <Weight size={22} />,
        cls: "bg-[#FCEEE2] text-[#C97A3C]",
        title: t("driver.metricEdit.titleTonnage"),
        step: 1,
        quick: [5, 10, 25],
      },
      distanceKm: {
        icon: <RouteIcon size={22} />,
        cls: "bg-[#E7EFFB] text-[#3B6FB6]",
        title: t("driver.metricEdit.titleDistance"),
        step: 1,
        quick: [10, 20, 50],
      },
      hours: {
        icon: <Clock size={22} />,
        cls: "bg-[#FCF3DC] text-[#C9952F]",
        title: t("driver.metricEdit.titleHours"),
        step: 0.25,
        quick: [0.5, 1],
      },
    };
    const cfg = icons[metricKey];

    setMetricConfig({
      metricKey,
      title: cfg.title,
      unit: meta.unit,
      icon: cfg.icon,
      iconClassName: cfg.cls,
      planned: meta.planned,
      actual: meta.actual,
      step: cfg.step,
      quickAdjust: cfg.quick,
    });
  };

  const saveMetric = async (value: number | undefined, note?: string) => {
    if (!metricConfig) return;
    await runAction(
      dispatch(updateMetric({ id: work._id, data: { metric: metricConfig.metricKey, value, note } })),
      () => toast.success(t("driver.toasts.metricSaved")),
    );
    setMetricConfig(null);
  };

  const onAddService = async (
    serviceTypeId: string,
    quantity: number,
    note?: string,
    extra?: { unitPrice?: number; nameSuffix?: string },
  ) => {
    // Snapshot the directory entry — name, unit, price and VAT category all ride
    // along from the catalogue (the driver no longer types a price). The backend
    // stores what we send. MATRIX services resolve their price from the chosen
    // zone × band cell (`extra.unitPrice`) and tag the line with that cell
    // (`extra.nameSuffix`) so the recorded line names the exact rate applied.
    const svc = serviceOptions.find((s) => s.id === serviceTypeId);
    const name =
      extra?.nameSuffix && svc?.name ? `${svc.name} — ${extra.nameSuffix}` : svc?.name;
    await runAction(
      dispatch(
        addService({
          id: work._id,
          data: {
            serviceTypeId,
            name,
            unit: svc?.unit,
            unitPrice: extra?.unitPrice ?? svc?.defaultPrice,
            tax: svc?.tax,
            quantity,
            note,
          },
        }),
      ),
      () => toast.success(t("driver.toasts.serviceAdded")),
    );
    setServicePickerOpen(false);
  };

  const onUpdateServiceQty = async (lineId: string, quantity: number) => {
    await runAction(
      dispatch(updateServiceQuantity({ id: work._id, lineId, data: { quantity: Math.max(0, quantity) } })),
      () => {},
    );
  };

  const isCyclic = work.type === "cyclic";
  const nextStep = work.type === "pickup_delivery" ? nextPickupDeliveryStep(work) : null;
  const allRoundsDone =
    isCyclic && !!work.roundsPlanned && (work.roundsActual ?? 0) >= work.roundsPlanned;
  // Dispatcher-ordered service lines the driver hasn't fulfilled yet — offered
  // for capture in the round-close sheet.
  // Invoiced lines are frozen at the billed quantity — never offered for a
  // round-close fill (the driver adds extra volume as a new service).
  const openPlannedServices = work.services.filter(
    (s) => s.status === "planned" && !s.invoiceId,
  );
  const perRoundDistanceHint =
    isCyclic && work.distanceKm.planned && work.roundsPlanned
      ? work.distanceKm.planned / work.roundsPlanned
      : undefined;

  // Disable action buttons while geo is capturing or a request is in flight —
  // this is what makes rapid round taps safe on the client (the backend also
  // enforces the round state machine as the source of truth).
  const actionDisabled = capturing || busy;

  return (
    <div className="min-h-screen bg-[#FAFAF8] pb-8">
      <div className="pt-safe bg-[#C97A3C] text-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate("/works")}
              className="p-1.5 -ml-1.5 rounded-lg hover:bg-white/15 transition-colors"
              aria-label={t("driver.exec.backToList")}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <div className="font-semibold truncate">{work.workNumber}</div>
              <div className="text-xs text-white/80">{t(`type.${work.type}`)}</div>
            </div>
          </div>
          <WorkStatusBadge status={work.status} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        {/* Route / object card */}
        <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-4 space-y-2.5">
          {hasRoute && (
            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[#9A3412]">
                {t("driver.card.from")}
              </div>
              <LocationView
                value={work.secondaryObject}
                iconColorClass="text-[#3F9461]"
                borderColorClass="border-l-[#3F9461]"
              />
            </div>
          )}
          <div>
            {hasRoute && (
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[#9A3412]">
                {t("driver.card.to")}
              </div>
            )}
            <LocationView
              value={work.primaryObject}
              iconColorClass={hasRoute ? "text-[#E24B4A]" : "text-[#C97A3C]"}
              borderColorClass={hasRoute ? "border-l-[#E24B4A]" : "border-l-[#C97A3C]"}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs text-[#9A3412]">
            <WorkTypeBadge type={work.type} />
            {work.material && <span>{work.material}</span>}
            <span>{getVehicleLabel(work)}</span>
          </div>
          {work.remarks && <p className="text-xs text-[#9A3412]">{work.remarks}</p>}
        </div>

        {/* Cyclic round counter */}
        {work.type === "cyclic" && (
          <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] border-2 border-[#F59E0B] rounded-xl p-4 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-[#92400E]">
              {t("driver.exec.round")}
            </div>
            <div className="text-3xl font-bold text-[#78350F]">
              {work.roundsActual ?? 0}
              {work.roundsPlanned ? ` / ${work.roundsPlanned}` : ""}
            </div>
          </div>
        )}

        {/* Action zone */}
        {!isCompleted && (
          <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
            {!started ? (
              <ActionButton
                onClick={handleStart}
                disabled={actionDisabled}
                tone="green"
                icon={<Play className="h-5 w-5" />}
                label={t("driver.exec.start")}
                capturing={capturing}
                t={t}
              />
            ) : work.type === "cyclic" ? (
              <>
                {work.roundPhase === "loaded" ? (
                  <ActionButton
                    onClick={() => setRoundSheetOpen(true)}
                    disabled={actionDisabled}
                    tone="green"
                    icon={<CheckCircle2 className="h-5 w-5" />}
                    label={t("driver.exec.markUnloaded", { n: (work.roundsActual ?? 0) + 1 })}
                    capturing={capturing}
                    t={t}
                  />
                ) : (
                  // Once every planned round is done the load button is removed
                  // entirely (not just disabled) — there is no round to start.
                  !allRoundsDone && (
                    <ActionButton
                      onClick={handleRoundLoaded}
                      disabled={actionDisabled}
                      tone="blue"
                      icon={<Package className="h-5 w-5" />}
                      label={t("driver.exec.markLoaded", { n: (work.roundsActual ?? 0) + 1 })}
                      capturing={capturing}
                      t={t}
                    />
                  )
                )}
                {allRoundsDone && work.roundPhase !== "loaded" && (
                  <p className="text-center text-xs text-[#3F9461] font-medium">
                    {t("driver.exec.allRoundsDone")}
                  </p>
                )}
                {work.roundPhase === "loaded" ? (
                  <p className="text-center text-xs text-[#C9952F] font-medium">
                    {t("driver.exec.unloadBeforeComplete")}
                  </p>
                ) : (work.roundsActual ?? 0) === 0 ? (
                  <p className="text-center text-xs text-[#C9952F] font-medium">
                    {t("driver.exec.completeNeedsRound")}
                  </p>
                ) : null}
                <CompleteButton
                  onClick={() => setCompleteConfirmOpen(true)}
                  disabled={
                    actionDisabled ||
                    work.roundPhase === "loaded" ||
                    (work.roundsActual ?? 0) === 0
                  }
                  t={t}
                />
              </>
            ) : work.type === "object" ? (
              <>
                {work.status === "paused" ? (
                  <ActionButton
                    onClick={handleResume}
                    disabled={actionDisabled}
                    tone="green"
                    icon={<Play className="h-5 w-5" />}
                    label={t("driver.exec.resume")}
                    capturing={capturing}
                    t={t}
                  />
                ) : (
                  <ActionButton
                    onClick={handlePause}
                    disabled={actionDisabled}
                    tone="amber"
                    icon={<Pause className="h-5 w-5" />}
                    label={t("driver.exec.pause")}
                    capturing={capturing}
                    t={t}
                  />
                )}
                <CompleteButton onClick={() => setCompleteConfirmOpen(true)} disabled={actionDisabled} t={t} />
              </>
            ) : (
              // pickup_delivery
              <>
                {nextStep ? (
                  <ActionButton
                    onClick={() => handleStep(nextStep)}
                    disabled={actionDisabled}
                    tone="blue"
                    icon={<Navigation className="h-5 w-5" />}
                    label={t(`driver.steps.${nextStep}`)}
                    capturing={capturing}
                    t={t}
                  />
                ) : (
                  <p className="text-center text-xs text-[#3F9461] font-medium">
                    {t("driver.exec.allStepsDone")}
                  </p>
                )}
                <CompleteButton
                  onClick={() => setCompleteConfirmOpen(true)}
                  disabled={actionDisabled || !!nextStep}
                  t={t}
                />
              </>
            )}
          </div>
        )}

        {isCompleted && (
          <div className="bg-[#E8F3EC] border border-[#A6D9BB] rounded-xl p-4 flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-[#3F9461] shrink-0" />
            <div>
              <div className="text-sm font-semibold text-[#2F7A4E]">{t("driver.exec.completedTitle")}</div>
              <div className="text-xs text-[#3F9461]">{t("driver.exec.completedSubtitle")}</div>
            </div>
          </div>
        )}

        {/* Metrics */}
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8A9590] mb-2">
            {t("driver.exec.metricsToday")}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <MetricRow
              icon={<Weight className="h-4 w-4" />}
              iconClassName="bg-[#FCEEE2] text-[#C97A3C]"
              label={t("metrics.tonnage")}
              actual={tonnageActual}
              planned={work.tonnage.planned}
              unit="t"
              manual={work.tonnage.autoCalcDisabled}
              locked={isCyclic}
              editable={!isCompleted && !isCyclic}
              onEdit={() => openMetricEditor("tonnage")}
              t={t}
            />
            <MetricRow
              icon={<RouteIcon className="h-4 w-4" />}
              iconClassName="bg-[#E7EFFB] text-[#3B6FB6]"
              label={t("metrics.distance")}
              actual={distanceActual}
              planned={work.distanceKm.planned}
              unit="km"
              manual={work.distanceKm.autoCalcDisabled}
              locked={isCyclic}
              editable={!isCompleted && !isCyclic}
              onEdit={() => openMetricEditor("distanceKm")}
              t={t}
            />
            <MetricRow
              icon={<Clock className="h-4 w-4" />}
              iconClassName="bg-[#FCF3DC] text-[#C9952F]"
              label={t("metrics.hours")}
              actual={hoursActual}
              planned={work.hours.planned}
              unit="h"
              isHours
              manual={work.hours.autoCalcDisabled}
              editable={false}
              t={t}
            />
            {/* Services summary card */}
            <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
              <div className="flex items-center gap-2">
                <span className="h-7 w-7 rounded-lg bg-[#F1E9FB] text-[#8B5CF6] flex items-center justify-center shrink-0">
                  <Package className="h-4 w-4" />
                </span>
                <span className="text-xs font-semibold uppercase tracking-wide text-[#8A9590]">
                  {t("metrics.services")}
                </span>
                <span className="ml-auto text-sm font-bold text-[#2B2F2E]">{work.services.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Services */}
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-2.5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#2B2F2E]">{t("driver.exec.services")}</h3>
            {/* Cyclic services are captured per round in the round-close sheet, so
                the standalone add/edit affordances are hidden here. The button is
                also hidden when no catalogue is available (nothing to add) — the
                banner below explains why. */}
            {!isCompleted && !isCyclic && serviceOptions.length > 0 && (
              <button
                type="button"
                onClick={() => setServicePickerOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-[#C97A3C] hover:text-[#B86B30]"
              >
                <Plus size={14} /> {t("driver.exec.addService")}
              </button>
            )}
          </div>

          {/* No catalogue for this work (client had none and General is empty
              too) — tell the driver to ask their company admin. Gated on the
              directory having loaded so it doesn't flash during the fetch, and
              hidden once completed (nothing left to add). */}
          {!isCompleted && servicesLoaded && serviceOptions.length === 0 && (
            <div className="flex items-start gap-2.5 rounded-lg bg-[#EFF4FB] border border-[#CBDCF2] px-3 py-2.5">
              <Info className="h-4 w-4 text-[#3B6FB6] shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-semibold text-[#2B4A73]">
                  {t("driver.exec.noCatalogTitle")}
                </div>
                <p className="text-[11px] text-[#3B6FB6] mt-0.5">
                  {t("driver.exec.noCatalogHint")}
                </p>
              </div>
            </div>
          )}
          {work.services.length === 0 ? (
            <p className="text-sm text-[#8A9590]">{t("driver.exec.noServices")}</p>
          ) : (
            <div className="space-y-2">
              {work.services.map((line) => {
                const qty = line.quantityActual ?? 0;
                const icon =
                  serviceOptions.find((o) => o.id === line.serviceTypeId)?.icon ??
                  getServiceIcon(line);
                return (
                  <div
                    key={line.id}
                    className="flex items-center gap-3 bg-[#FAF9F6] border border-[#ECEAE4] rounded-lg px-3 py-2"
                  >
                    <span className="text-lg shrink-0">{icon}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-[#2B2F2E] truncate">{line.name}</div>
                      <div className="text-xs text-[#8A9590]">
                        €{line.unitPrice ?? 0} × {qty} {line.unit} = €{Math.round((line.unitPrice ?? 0) * qty * 100) / 100}
                      </div>
                      {line.quantityPlanned != null && (
                        <div className="text-[11px] font-medium text-[#C97A3C]">
                          {t("driver.exec.ordered", { qty: line.quantityPlanned, unit: line.unit })}
                        </div>
                      )}
                    </div>
                    {/* An invoiced line is frozen at the billed quantity — no
                        stepper, no hint of editability (extra volume goes on as
                        a NEW service; corrections go through the admin). A line
                        locked into a closed round shows its round tag and is
                        read-only; the free +/- steppers only apply to the
                        single-capture non-cyclic flow. */}
                    {line.invoiceId ? (
                      <span className="w-6 text-center text-sm font-semibold shrink-0">
                        {Number.isInteger(qty) ? qty : qty.toFixed(1)}
                      </span>
                    ) : line.roundIndex != null ? (
                      <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#EEECE6] text-[#5A6460]">
                        {t("driver.exec.roundTag", { n: line.roundIndex })}
                      </span>
                    ) : (
                      !isCompleted &&
                      !isCyclic && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => onUpdateServiceQty(line.id, qty - 0.5)}
                            className="h-7 w-7 rounded-md border border-[#E5E3DD] bg-white flex items-center justify-center text-[#5A6460] hover:border-[#C97A3C]"
                            aria-label={t("driver.metricEdit.decrease")}
                          >
                            <Minus size={14} />
                          </button>
                          <span className="w-6 text-center text-sm font-semibold">
                            {Number.isInteger(qty) ? qty : qty.toFixed(1)}
                          </span>
                          <button
                            type="button"
                            onClick={() => onUpdateServiceQty(line.id, qty + 0.5)}
                            className="h-7 w-7 rounded-md border border-[#E5E3DD] bg-white flex items-center justify-center text-[#5A6460] hover:border-[#C97A3C]"
                            aria-label={t("driver.metricEdit.increase")}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Activity / geo log */}
        <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowLog((s) => !s)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-[#2B2F2E]"
          >
            <span>
              {t("driver.exec.activityLog")}{" "}
              <span className="text-[#8A9590] font-normal">({work.events.length})</span>
            </span>
            <ChevronDown className={`h-4 w-4 text-[#8A9590] transition-transform ${showLog ? "rotate-180" : ""}`} />
          </button>
          {showLog && (
            <div className="px-4 pb-4">
              {work.events.length === 0 ? (
                <p className="text-sm text-[#8A9590]">{t("driver.exec.noEvents")}</p>
              ) : (
                <ol className="space-y-2.5">
                  {[...work.events].reverse().map((event) => {
                    const coords = formatCoords(event.coordinates);
                    return (
                      <li key={event.id} className="flex items-start gap-2.5 text-sm">
                        <span className="mt-0.5 h-2 w-2 rounded-full bg-[#C97A3C] shrink-0" />
                        <div className="min-w-0">
                          <div className="text-[#2B2F2E]">
                            {event.label || t(`driver.events.${event.type}`)}
                          </div>
                          <div className="text-xs text-[#8A9590] flex items-center gap-2 flex-wrap">
                            <span>{formatTime(event.timestamp)}</span>
                            {coords ? (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="h-3 w-3 text-[#3F9461]" /> {coords}
                              </span>
                            ) : (
                              <span className="text-[#C9952F]">{t("driver.exec.noGeo")}</span>
                            )}
                            {event.note && <span className="italic">“{event.note}”</span>}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}
        </div>
      </div>

      <MetricEditSheet config={metricConfig} onClose={() => setMetricConfig(null)} onSave={saveMetric} />
      <ServicePicker
        isOpen={servicePickerOpen}
        onClose={() => setServicePickerOpen(false)}
        serviceTypes={serviceOptions}
        onAdd={onAddService}
      />
      <RoundCloseSheet
        isOpen={roundSheetOpen}
        roundNumber={(work.roundsActual ?? 0) + 1}
        plannedServices={openPlannedServices}
        serviceOptions={serviceOptions}
        servicesLoaded={servicesLoaded}
        perRoundDistanceHint={perRoundDistanceHint}
        busy={actionDisabled}
        onClose={() => setRoundSheetOpen(false)}
        onConfirm={submitRoundClose}
      />
      <CompleteConfirmModal
        isOpen={completeConfirmOpen}
        autoHours={hoursActual}
        showTonnage={work.type === "pickup_delivery"}
        currentTonnage={work.tonnage.actual}
        unfulfilledServices={openPlannedServices}
        busy={actionDisabled}
        onClose={() => setCompleteConfirmOpen(false)}
        onConfirm={handleComplete}
        t={t}
      />
    </div>
  );
};

// ---- small presentational helpers ----------------------------------------

interface ActionButtonProps {
  onClick: () => void;
  disabled: boolean;
  tone: "green" | "blue" | "amber";
  icon: React.ReactNode;
  label: string;
  capturing: boolean;
  t: (key: string) => string;
}

const TONES: Record<ActionButtonProps["tone"], string> = {
  green: "bg-[#059669] hover:bg-[#047857]",
  blue: "bg-[#3B6FB6] hover:bg-[#335f9e]",
  amber: "bg-[#D97706] hover:bg-[#b45309]",
};

const ActionButton: React.FC<ActionButtonProps> = ({ onClick, disabled, tone, icon, label, capturing, t }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-semibold text-[15px] transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${TONES[tone]}`}
  >
    {capturing ? (
      <span className="inline-flex items-center gap-2">
        <Navigation className="h-4 w-4 animate-pulse" /> {t("driver.geo.capturing")}
      </span>
    ) : (
      <>
        {icon}
        {label}
      </>
    )}
  </button>
);

const CompleteButton: React.FC<{ onClick: () => void; disabled: boolean; t: (k: string) => string }> = ({
  onClick,
  disabled,
  t,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#A6D9BB] bg-[#E8F3EC] text-[#2F7A4E] font-semibold text-sm hover:bg-[#DCEEE3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <CheckCircle2 className="h-4 w-4" /> {t("driver.exec.complete")}
  </button>
);

interface MetricRowProps {
  icon: React.ReactNode;
  iconClassName: string;
  label: string;
  actual?: number;
  planned?: number;
  unit: string;
  isHours?: boolean;
  manual?: boolean;
  /** Round-derived metric the driver can't edit directly — shows a "Locked" badge. */
  locked?: boolean;
  editable: boolean;
  onEdit?: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const MetricRow: React.FC<MetricRowProps> = ({
  icon,
  iconClassName,
  label,
  actual,
  planned,
  unit,
  isHours,
  manual,
  locked,
  editable,
  onEdit,
  t,
}) => {
  const fmt = (v?: number) =>
    v === undefined ? "—" : isHours ? formatHours(v) : Number.isInteger(v) ? `${v}` : v.toFixed(1);

  // When the driver records more than was planned, "4 / 2" reads like a broken
  // progress fraction — so flag the overage explicitly instead.
  const over = actual !== undefined && planned !== undefined && actual > planned;

  return (
    <button
      type="button"
      onClick={editable ? onEdit : undefined}
      className={`text-left bg-white border rounded-xl p-3 transition-colors ${
        over ? "border-[#E9B782] bg-[#FFFBF6]" : "border-[#ECEAE4]"
      } ${editable ? "hover:border-[#C97A3C] cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center gap-2">
        <span className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${iconClassName}`}>{icon}</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-[#8A9590]">{label}</span>
        <span
          className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded ${
            locked
              ? "bg-[#EEECE6] text-[#5A6460]"
              : manual
                ? "bg-[#FCF3DC] text-[#92400E]"
                : "bg-[#E8F3EC] text-[#2F7A4E]"
          }`}
        >
          {locked ? t("driver.exec.locked") : manual ? t("driver.exec.manual") : t("driver.exec.auto")}
        </span>
      </div>
      <div className="mt-1.5 text-sm flex items-center flex-wrap gap-x-1">
        <span className={`font-bold ${over ? "text-[#C97A3C]" : "text-[#2B2F2E]"}`}>{fmt(actual)}</span>
        {planned !== undefined && (
          <span className="text-[#8A9590]">/ {fmt(planned)}</span>
        )}
        {!isHours && <span className="text-[#8A9590] text-xs">{unit}</span>}
        {over && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FCEEE2] text-[#C97A3C]">
            {t("metrics.overBadge", { n: fmt(actual! - planned!) })}
          </span>
        )}
      </div>
    </button>
  );
};

interface CompleteConfirmModalProps {
  isOpen: boolean;
  autoHours?: number;
  /** Show an optional tonnage field (pickup_delivery) the driver can fill before finishing. */
  showTonnage?: boolean;
  currentTonnage?: number;
  /** Dispatcher-ordered service lines still unfulfilled — surfaced as a warning. */
  unfulfilledServices: ServiceLine[];
  busy: boolean;
  onClose: () => void;
  onConfirm: (hours?: number, tonnage?: number) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

/**
 * Completion confirm — shows the system-tracked hours and lets the driver make a
 * single optional correction before the work is finalized (hours never get a
 * free editor during execution). Also the auto-complete prompt after the last
 * cyclic round.
 */
const CompleteConfirmModal: React.FC<CompleteConfirmModalProps> = ({
  isOpen,
  autoHours,
  showTonnage = false,
  currentTonnage,
  unfulfilledServices,
  busy,
  onClose,
  onConfirm,
  t,
}) => {
  const [adjusting, setAdjusting] = useState(false);
  const [hours, setHours] = useState("");
  const [tonnage, setTonnage] = useState("");

  useEffect(() => {
    if (isOpen) {
      setAdjusting(false);
      setHours(autoHours != null ? String(autoHours) : "");
      setTonnage(currentTonnage != null ? String(currentTonnage) : "");
    }
  }, [isOpen, autoHours, currentTonnage]);

  if (!isOpen) return null;

  const parsed = Number(hours);
  const valid = Number.isFinite(parsed) && parsed > 0;
  const parsedTonnage = Number(tonnage);
  const tonnageValue =
    showTonnage &&
    tonnage !== "" &&
    Number.isFinite(parsedTonnage) &&
    parsedTonnage >= 0 &&
    parsedTonnage !== currentTonnage
      ? parsedTonnage
      : undefined;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("driver.exec.completeConfirmTitle")} size="sm">
      <div className="flex-1 overflow-y-auto px-4 py-4 sm:p-6 space-y-4">
        <p className="text-sm text-[#5A6460]">{t("driver.exec.completeConfirmBody")}</p>

        {/* Ordered-but-unrecorded services — warn, don't block. */}
        {unfulfilledServices.length > 0 && (
          <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-3">
            <div className="flex items-center gap-2 text-[#9A3412]">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="text-xs font-semibold">
                {t("driver.exec.unfulfilledTitle", { count: unfulfilledServices.length })}
              </span>
            </div>
            <ul className="mt-2 space-y-1">
              {unfulfilledServices.map((line) => (
                <li key={line.id} className="text-xs text-[#9A3412] flex items-center gap-1.5">
                  <span className="h-1 w-1 rounded-full bg-[#C2410C]" />
                  <span className="font-medium">{line.name}</span>
                  {line.quantityPlanned != null && (
                    <span className="text-[#C2410C]">
                      · {t("driver.exec.ordered", { qty: line.quantityPlanned, unit: line.unit })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-[#B45309]">{t("driver.exec.unfulfilledHint")}</p>
          </div>
        )}

        {showTonnage && (
          <div className="bg-[#FAF9F6] border border-[#ECEAE4] rounded-xl p-3">
            <div className="flex items-center gap-2">
              <span className="h-7 w-7 rounded-lg bg-[#FCEEE2] text-[#C97A3C] flex items-center justify-center">
                <Weight className="h-4 w-4" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wide text-[#8A9590]">
                {t("metrics.tonnage")}
              </span>
              <span className="ml-auto text-[10px] font-medium text-[#8A9590]">
                {t("driver.exec.optional")}
              </span>
            </div>
            <div className="mt-3 flex items-stretch rounded-lg border-2 border-[#C97A3C]/40 overflow-hidden">
              <input
                type="number"
                min={0}
                step={0.1}
                inputMode="decimal"
                value={tonnage}
                onChange={(e) => setTonnage(e.target.value)}
                placeholder="0"
                className="flex-1 text-center text-xl font-bold py-2 bg-transparent focus:outline-none"
              />
              <div className="px-3 flex items-center bg-white border-l border-[#ECEAE4] text-[#8A9590] font-semibold">
                t
              </div>
            </div>
          </div>
        )}

        <div className="bg-[#FAF9F6] border border-[#ECEAE4] rounded-xl p-3">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-[#FCF3DC] text-[#C9952F] flex items-center justify-center">
              <Clock className="h-4 w-4" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wide text-[#8A9590]">
              {t("driver.exec.hoursReview")}
            </span>
            <span className="ml-auto text-base font-bold text-[#2B2F2E]">
              {autoHours != null ? formatHours(autoHours) : "—"}
            </span>
          </div>
          {adjusting ? (
            <div className="mt-3 flex items-stretch rounded-lg border-2 border-[#C97A3C] overflow-hidden">
              <input
                type="number"
                min={0}
                step={0.25}
                inputMode="decimal"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="0"
                className="flex-1 text-center text-xl font-bold py-2 bg-transparent focus:outline-none"
              />
              <div className="px-3 flex items-center bg-white border-l border-[#ECEAE4] text-[#8A9590] font-semibold">
                h
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAdjusting(true)}
              className="mt-2 text-xs font-medium text-[#C97A3C] hover:text-[#B86B30]"
            >
              {t("driver.exec.adjustHours")}
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 px-4 py-3 sm:px-6 border-t border-[rgba(11,15,14,0.08)] bg-white shrink-0">
        <Button variant="outline" onClick={onClose}>
          {t("driver.exec.completeConfirmCancel")}
        </Button>
        <Button
          variant="primary"
          disabled={busy || (adjusting && !valid)}
          onClick={() =>
            onConfirm(adjusting && valid ? parsed : undefined, tonnageValue)
          }
        >
          {t("driver.exec.completeConfirmConfirm")}
        </Button>
      </div>
    </Modal>
  );
};

export default DriverWorkExecutionPage;
